/**
 * Aritmética de dinero.
 *
 * Regla del proyecto: adentro del motor **nunca** hay decimales flotantes. Todo importe
 * se representa como un entero de unidades mínimas ("centavos"). Los strings que vienen
 * de la API de Tienda Nube se parsean a entero al entrar y se formatean al salir.
 *
 * Un `1234.50 * 1.12` en punto flotante da `1382.1600000000003`. Con enteros no.
 */
/** Decimales de la moneda. ARS usa 2. Ver spike 6 en docs/hallazgos-api.md. */
export const DEFAULT_DECIMALS = 2;
/**
 * Sólo dígitos, punto decimal opcional y signo menos opcional.
 * Rechaza a propósito: "1.234,50", "1,234.50", "$1000", "1e5", "".
 */
const PRICE_PATTERN = /^-?\d+(?:\.\d+)?$/;
/** Multiplicador de la moneda: 100 para 2 decimales. */
export function minorUnit(decimals = DEFAULT_DECIMALS) {
    if (!Number.isInteger(decimals) || decimals < 0 || decimals > 6) {
        throw new RangeError(`decimales fuera de rango: ${decimals}`);
    }
    return 10 ** decimals;
}
/**
 * Parsea un precio de la API a unidades mínimas.
 *
 * Devuelve `null` cuando el valor es ausente o no parseable — nunca lanza y nunca
 * inventa un 0. Un `null` acá significa "esta variante no tiene precio", que es un
 * estado real de Tienda Nube (producto en "consultar precio"), no un error.
 *
 * Si el string trae más decimales que la moneda, redondea a la mitad hacia arriba.
 */
export function parsePrice(raw, decimals = DEFAULT_DECIMALS) {
    if (raw === null || raw === undefined)
        return null;
    const text = typeof raw === 'number' ? (Number.isFinite(raw) ? String(raw) : '') : raw.trim();
    if (text === '' || !PRICE_PATTERN.test(text))
        return null;
    const negative = text.startsWith('-');
    const unsigned = negative ? text.slice(1) : text;
    const dot = unsigned.indexOf('.');
    const integerPart = dot === -1 ? unsigned : unsigned.slice(0, dot);
    const fractionPart = dot === -1 ? '' : unsigned.slice(dot + 1);
    // Un dígito extra para poder redondear a la mitad hacia arriba.
    const padded = fractionPart.padEnd(decimals + 1, '0');
    const kept = padded.slice(0, decimals);
    const guard = padded.charCodeAt(decimals) - 48;
    let minor = Number(integerPart) * minorUnit(decimals) + (kept === '' ? 0 : Number(kept));
    if (guard >= 5)
        minor += 1;
    if (!Number.isSafeInteger(minor))
        return null;
    return negative ? -minor : minor;
}
/**
 * Formatea unidades mínimas al string que espera la API: punto decimal, sin separador
 * de miles, con la cantidad exacta de decimales de la moneda. Ej: `123450` → `"1234.50"`.
 *
 * Error conocido de la API si esto sale mal: `"The price must be a number"`.
 */
export function formatPrice(value, decimals = DEFAULT_DECIMALS) {
    if (!Number.isInteger(value)) {
        throw new TypeError(`formatPrice espera un entero de unidades mínimas, llegó ${value}`);
    }
    const unit = minorUnit(decimals);
    const negative = value < 0;
    const absolute = Math.abs(value);
    const whole = Math.trunc(absolute / unit);
    const sign = negative ? '-' : '';
    if (decimals === 0)
        return `${sign}${whole}`;
    const fraction = String(absolute % unit).padStart(decimals, '0');
    return `${sign}${whole}.${fraction}`;
}
/**
 * Redondea a entero con la mitad **alejándose del cero**, que es la convención
 * comercial. `Math.round(-2.5)` da `-2`; esto da `-3`.
 */
export function roundToMinor(value) {
    if (!Number.isFinite(value)) {
        throw new RangeError(`roundToMinor recibió un valor no finito: ${value}`);
    }
    return value < 0 ? -Math.round(-value) : Math.round(value);
}
/**
 * Variación porcentual entre dos importes. `null` si no se puede calcular
 * (falta alguno de los dos, o el original es 0 y la división no tiene sentido).
 */
export function percentDelta(from, to) {
    if (from === null || to === null || from === 0)
        return null;
    return ((to - from) / Math.abs(from)) * 100;
}
/**
 * Unidades mínimas → pesos, como los lee un comerciante argentino. `7995000` → `"$ 79.950"`.
 *
 * *** Es para textos de cara al comerciante, nunca para la API. *** Para eso está
 * `formatPrice`, que emite `"79950.00"` sin separador de miles: mandar esto a Tienda Nube
 * devuelve `"The price must be a number"`.
 *
 * Los centavos se muestran sólo si los hay. Un precio de góndola argentino termina en `,00`
 * casi siempre, y arrastrarlo hace más difícil comparar dos números en una misma oración,
 * que es justamente lo que pide la alerta de la oferta.
 */
export function pesos(value, decimals = DEFAULT_DECIMALS) {
    const unit = minorUnit(decimals);
    const centavos = Math.abs(value) % unit;
    const formato = new Intl.NumberFormat('es-AR', {
        minimumFractionDigits: centavos === 0 ? 0 : decimals,
        maximumFractionDigits: centavos === 0 ? 0 : decimals,
    });
    return `$ ${formato.format(value / unit)}`;
}
//# sourceMappingURL=money.js.map